import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ArrowLeft, Lock, Unlock, Check, BarChart3 } from "lucide-react";
import {
  EVAL_LEVEL_LABELS,
  type EvaluationItem,
  type QuestionnaireResponseInput,
  type SessionUser,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface EvaluationDetail {
  evaluation: {
    id: string;
    templateId: string;
    level: "client_user" | "organizational" | "team" | "project";
    projectId: string | null;
    period: string;
    createdAt: number;
    updatedAt: number;
    closedAt: number | null;
  };
  template: {
    id: string;
    name: string;
    visibility: "everyone" | "lead" | "finanzas" | "admin_only";
    isAnonymous: boolean;
    description: string | null;
    items: EvaluationItem[];
  };
  myResponse: {
    id: string;
    answers: Record<string, { value: number | string; comment?: string }>;
  } | null;
  responsesCount: number;
  aggregate: {
    quantStats: Record<string, { count: number; avg: number; min: number; max: number }>;
    qualResponses: Record<string, string[]>;
  } | null;
  canSeeIndividualResponses: boolean;
}

export const Route = createFileRoute("/evaluations/$id")({
  component: EvaluationDetailPage,
});

function EvaluationDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });

  const { data, isLoading } = useQuery<EvaluationDetail>({
    queryKey: ["evaluation", id],
    queryFn: () => api.get(`/evaluations/${id}`),
  });

  const close = useMutation({
    mutationFn: () => api.patch(`/evaluations/${id}/close`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["evaluation", id] }),
  });

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  const { evaluation, template, myResponse, responsesCount, aggregate } = data;
  const isOpen = evaluation.closedAt === null;
  const canClose = isOpen && (me.data?.role === "admin" || me.data?.role === "lead");

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        eyebrow={`${EVAL_LEVEL_LABELS[evaluation.level]} · ${evaluation.period}`}
        title={template.name}
        subtitle={template.description ?? undefined}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/evaluations" className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Volver
            </Link>
            {canClose && (
              <button
                type="button"
                className="btn btn-warning btn-sm"
                onClick={() => {
                  if (confirm("¿Cerrar esta evaluación? No se podrán enviar más respuestas.")) {
                    close.mutate();
                  }
                }}
              >
                <Lock className="size-3.5" /> Cerrar
              </button>
            )}
          </div>
        }
      />

      <div className="card-elevated p-4 flex items-center gap-3 text-sm">
        {isOpen ? (
          <span className="inline-flex items-center gap-1.5 text-success">
            <Unlock className="size-4" /> Abierta — desde el {formatDate(evaluation.createdAt)}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-base-content/55">
            <Lock className="size-4" /> Cerrada el {formatDate(evaluation.closedAt!)}
          </span>
        )}
        <span className="text-base-content/40">·</span>
        <span className="text-base-content/65">{responsesCount} respuestas</span>
        {template.visibility !== "everyone" && (
          <>
            <span className="text-base-content/40">·</span>
            <span className="inline-flex items-center gap-1 text-warning">
              <Lock className="size-3.5" /> {template.visibility}
            </span>
          </>
        )}
        {template.isAnonymous && (
          <>
            <span className="text-base-content/40">·</span>
            <span className="inline-flex items-center gap-1 text-info">
              <Lock className="size-3.5" /> anónima
            </span>
          </>
        )}
      </div>

      {isOpen ? (
        <ResponseForm evaluationId={id} items={template.items} existing={myResponse?.answers} />
      ) : (
        <div className="card-elevated p-5 text-sm text-base-content/65">
          La evaluación está cerrada — ya no se pueden enviar respuestas. Los agregados se muestran
          abajo.
        </div>
      )}

      {aggregate && (
        <Aggregate items={template.items} aggregate={aggregate} responsesCount={responsesCount} />
      )}
    </div>
  );
}

// ─── Response form ────────────────────────────────────────────────────────

function ResponseForm({
  evaluationId,
  items,
  existing,
}: {
  evaluationId: string;
  items: EvaluationItem[];
  existing?: Record<string, { value: number | string; comment?: string }>;
}) {
  const qc = useQueryClient();
  const [answers, setAnswers] = useState<
    Record<string, { value: number | string; comment?: string }>
  >({});

  // Cargar respuesta existente si la hay
  useEffect(() => {
    if (existing) setAnswers(existing);
  }, [existing]);

  const submit = useMutation({
    mutationFn: (input: QuestionnaireResponseInput) =>
      api.post(`/evaluations/${evaluationId}/responses`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["evaluation", evaluationId] }),
  });

  const setAnswer = (itemId: string, value: number | string, comment?: string) => {
    setAnswers((prev) => ({
      ...prev,
      [itemId]: { value, comment: comment ?? prev[itemId]?.comment },
    }));
  };

  const handleSubmit = () => {
    submit.mutate({ answers });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="space-y-4"
    >
      {items.map((item, i) => (
        <div key={item.id} className="card-elevated p-5 space-y-3">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-semibold text-primary tabular-nums">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="text-sm font-medium leading-snug">
              {item.prompt}
              {item.required && <span className="text-error ml-0.5">*</span>}
            </h3>
          </div>

          {item.kind === "quant" ? (
            <QuantInput item={item} value={answers[item.id]?.value} onChange={setAnswer} />
          ) : (
            <QualInput item={item} value={answers[item.id]?.value} onChange={setAnswer} />
          )}

          <input
            type="text"
            placeholder="Comentario adicional (opcional)"
            className="input input-bordered input-sm w-full text-sm"
            value={answers[item.id]?.comment ?? ""}
            onChange={(e) =>
              setAnswers((prev) => ({
                ...prev,
                [item.id]: {
                  value: prev[item.id]?.value ?? (item.kind === "quant" ? 0 : ""),
                  comment: e.target.value,
                },
              }))
            }
          />
        </div>
      ))}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <button type="submit" className="btn btn-primary" disabled={submit.isPending}>
          {submit.isPending && <span className="loading loading-spinner loading-xs" />}
          {existing ? <Check className="size-4" /> : null}
          {existing ? "Actualizar respuesta" : "Enviar respuesta"}
        </button>
      </div>

      {submit.isSuccess && (
        <div className="alert alert-success py-2 text-sm">
          <Check className="size-4" />
          <span>Respuesta guardada. Puedes editarla mientras la evaluación esté abierta.</span>
        </div>
      )}
    </form>
  );
}

function QuantInput({
  item,
  value,
  onChange,
}: {
  item: Extract<EvaluationItem, { kind: "quant" }>;
  value: number | string | undefined;
  onChange: (id: string, v: number | string) => void;
}) {
  const num = typeof value === "number" ? value : item.scale.min;
  return (
    <div>
      <input
        type="range"
        min={item.scale.min}
        max={item.scale.max}
        step={1}
        value={num}
        onChange={(e) => onChange(item.id, parseInt(e.target.value, 10))}
        className="range range-primary range-sm w-full"
      />
      <div className="flex items-center justify-between mt-2 text-xs text-base-content/55">
        <span>{item.scale.minLabel ?? item.scale.min}</span>
        <span className="text-lg font-bold text-primary tabular-nums">{num}</span>
        <span>{item.scale.maxLabel ?? item.scale.max}</span>
      </div>
    </div>
  );
}

function QualInput({
  item,
  value,
  onChange,
}: {
  item: Extract<EvaluationItem, { kind: "qual" }>;
  value: number | string | undefined;
  onChange: (id: string, v: number | string) => void;
}) {
  return (
    <textarea
      rows={3}
      className="textarea textarea-bordered w-full"
      placeholder={item.placeholder ?? "Tu respuesta…"}
      value={typeof value === "string" ? value : ""}
      onChange={(e) => onChange(item.id, e.target.value)}
    />
  );
}

// ─── Aggregate ────────────────────────────────────────────────────────────

function Aggregate({
  items,
  aggregate,
  responsesCount,
}: {
  items: EvaluationItem[];
  aggregate: EvaluationDetail["aggregate"];
  responsesCount: number;
}) {
  if (!aggregate || responsesCount === 0) return null;
  return (
    <div className="card-elevated p-5 space-y-4">
      <div className="flex items-center gap-2">
        <BarChart3 className="size-4 text-primary" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
          Agregado · {responsesCount} respuestas
        </h3>
      </div>

      {items.map((item) => {
        if (item.kind === "quant") {
          const stats = aggregate.quantStats[item.id];
          if (!stats || stats.count === 0) return null;
          const pct = ((stats.avg - item.scale.min) / (item.scale.max - item.scale.min)) * 100;
          return (
            <div key={item.id} className="space-y-1.5">
              <div className="text-sm font-medium">{item.prompt}</div>
              <div className="flex items-center gap-3">
                <div className="text-2xl font-bold text-primary tabular-nums">{stats.avg}</div>
                <div className="text-xs text-base-content/55 leading-tight">
                  promedio
                  <br />
                  <span className="tabular-nums">
                    min {stats.min} · max {stats.max} · {stats.count} resp.
                  </span>
                </div>
                <div className="flex-1 h-2 rounded-full bg-base-200 overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                  />
                </div>
              </div>
            </div>
          );
        }
        const responses = aggregate.qualResponses[item.id] ?? [];
        if (responses.length === 0) return null;
        return (
          <div key={item.id} className="space-y-2">
            <div className="text-sm font-medium">{item.prompt}</div>
            <ul className="space-y-1.5">
              {responses.map((r, i) => (
                <li
                  key={i}
                  className="text-sm text-base-content/80 leading-relaxed pl-3 border-l-2 border-primary/30"
                >
                  {r}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
